import { observable, action } from "mobx";
import { makeApi, URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";

// Some names are stored HTML-escaped ("SG &amp; India"); a textarea decodes entities without running anything.
export const unescapeName = name => {
  if (!name || !String(name).includes("&")) return name;
  const t = document.createElement("textarea");
  t.innerHTML = name;
  return t.value;
};

/**
 * Store for the Agents feature (user-configured workflow automations).
 * Talks to the internal /cm/agents API. Mirrors the app's other MobX stores:
 * observable state + async actions using makeApi(config).
 */
// A signal agent also carries a seller profile (what it writes for); only the ICP prospector needs an ICP.
export const isIcpAgent = a =>
  a.templateKey === "daily_icp_prospector" || !!(a.config && a.config.sellerProfileId && (a.trigger || {}).type !== "signal.saved");
/** A draft ICP agent that can't run until its ICP is approved. */
export const needsIcp = a => isIcpAgent(a) && !(a.config && a.config.icpApproved) && a.status !== "ACTIVE";
/** The agents page's "Needs you": an overview card waiting on an approval, failing, or a draft one step from running. */
export const needsYou = c => c.state === "WAITING_APPROVAL" || c.state === "FAILING" || needsIcp(c.agent || {});
/** How many agents need you, as the agents page header counts them (archived ones don't). */
export const needsYouCount = cards => (cards || []).filter(c => needsYou(c) && (c.agent || {}).status !== "ARCHIVED").length;

/** Why a team with this usage ({enabled, eligible, used}) can't have another agent. */
export const agentLimitMessage = usage =>
  usage && usage.eligible > 0
    ? `Your team has reached its limit of ${usage.eligible} ${usage.eligible === 1 ? "agent" : "agents"}. Archive or delete an agent, or contact us to add more.`
    : "Your team's plan doesn't include agents yet. Contact us to add them.";

class AgentsData {
  @observable agents = [];
  @observable agentsLoading = false;
  @observable currentAgent = null;
  @observable runs = [];
  @observable lastRun = null;
  @observable runRecords = {};
  @observable runRecordsLoading = {};
  @observable icpPreview = null;
  @observable busy = false;
  @observable testing = false; // a dry run in progress (separate from busy, which Run now / approvals use)
  @observable error = "";
  // The team's agent access: {enabled, eligible, used}.
  @observable usage = null;
  @observable approvals = [];
  @observable approvalsLoading = false;
  @observable pendingApprovalCount = 0;
  @observable folders = [];
  // The agents page in one call: {agents:[{agent,state,activeRun,lastRun,recentRuns,totals,
  // waitingContacts,nextRunAt,failedInARow}], summary:{live,needsYou,foundThisWeek,enrolledThisWeek}}.
  @observable overview = null;
  @observable overviewLoading = false;
  // The server's reason when agents are off for the team (code agents_disabled): not a retryable failure.
  @observable overviewDenied = null;
  // The run "Watch live" is showing, refreshed by polling while it's open.
  @observable liveRun = null;
  // A draft agent spec composed by AI from a description, handed to the builder to prefill.
  @observable generatedDraft = null;
  // The words the user actually typed to get that draft. Shown on the review page so they can
  // check the flow against what they asked for; deliberately never saved on the agent — once it
  // exists, the agent's own summary is the description of it.
  @observable generatedPrompt = "";
  // Source pickers for the flow builder.
  @observable contactLists = [];
  @observable savedSearches = [];
  // CRMs an Export to CRM step can use: [{value, label, connected}].
  @observable crms = [];
  @observable triggerTypes = [];
  @observable contactStages = [];
  @observable contactFields = [];
  // Slack channels an agent's notifications can be pointed at. Empty when Slack isn't connected.
  @observable slackChannels = [];
  @observable slackChannelsLoaded = false;
  @observable sequencesLite = [];
  @observable accountLists = [];
  @observable teamMembers = []; // for "Get a report" → one team member
  @observable teamGroups = [];  // …or a group
  // Field vocabulary for the condition editor (branch.if / filter.condition).
  @observable conditionFields = [];

  fill(url, params = {}) {
    let filled = url;
    Object.keys(params).forEach(k => {
      filled = filled.replace(`<<${k}>>`, encodeURIComponent(params[k]));
    });
    return filled;
  }

  ok(res) {
    return res && res.status === 200;
  }

  // A create that failed: say why (e.g. the team's agent limit), refreshing usage when it's the limit.
  createFailed(res) {
    const body = (res && res.response && res.response.data) || {};
    const msg = body.error || "Could not create the agent.";
    if (body.code === "agent_limit_reached") this.fetchUsage();
    toasterState.setToastMsg(msg, "failure");
    return msg;
  }

  // Before starting a new agent (Hire, a template, Build it): false — with the reason shown — when the
  // team has no room for another, so the user isn't taken through setup only to be refused on create.
  async canHireAgent() {
    const usage = await this.fetchUsage();
    if (usage && usage.enabled && usage.used >= usage.eligible) {
      toasterState.setToastMsg(agentLimitMessage(usage), "failure");
      return false;
    }
    return true;
  }

  @action.bound
  async fetchUsage() {
    const res = await makeApi({ url: URLS.agentUsage, method: "GET" });
    if (this.ok(res) && res.data) this.usage = res.data;
    return this.usage;
  }

  @action.bound
  async fetchAgents() {
    this.agentsLoading = true;
    const res = await makeApi({ url: URLS.agentsList, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) {
      this.agents = res.data;
    }
    this.agentsLoading = false;
    return this.agents;
  }

  @action.bound
  async fetchOverview() {
    this.overviewLoading = true;
    const res = await makeApi({ url: URLS.agentsOverview, method: "GET" });
    if (this.ok(res) && res.data && Array.isArray(res.data.agents)) {
      this.overview = res.data;
      // Keep the plain list in step, so the side panel and detail seeding see the same agents.
      this.agents = res.data.agents.map(c => c.agent);
    }
    const body = (res && res.response && res.response.data) || {};
    this.overviewDenied = body.code === "agents_disabled" ? body.error || "Agents aren't on for your team yet." : null;
    this.overviewLoading = false;
    return this.overview;
  }

  @action.bound
  clearLiveRun() {
    this.liveRun = null;
  }

  // Start a real run and hand it back straight away (no waiting), for "Run now" → Watch live.
  @action.bound
  async startRun(id, inputs) {
    const res = await makeApi({ url: this.fill(URLS.agentRun, { id }), method: "POST", data: inputs || {} });
    if (this.ok(res) && res.data) return res.data;
    const body = (res && res.response && res.response.data) || {};
    toasterState.setToastMsg(body.error || "Couldn't start the run.", "failure");
    return null;
  }

  @action.bound
  async fetchRun(id, runId) {
    const res = await makeApi({ url: this.fill(URLS.agentRunDetail, { id, runId }), method: "GET" });
    if (this.ok(res) && res.data) {
      this.liveRun = res.data;
      return res.data;
    }
    // null only when the server says the run isn't there; a 502 or timeout is worth another poll.
    const status = res && res.response && res.response.status;
    return status === 404 || status === 403 || status === 400 ? null : undefined;
  }

  // "Stop run": contacts already enrolled stay enrolled; the run ends before its next step.
  @action.bound
  async stopRun(id, runId) {
    const res = await makeApi({ url: this.fill(URLS.agentRunCancel, { id, runId }), method: "POST" });
    if (this.ok(res) && res.data) {
      this.liveRun = res.data;
      return res.data;
    }
    const body = (res && res.response && res.response.data) || {};
    toasterState.setToastMsg(body.error || "Couldn't stop this run.", "failure");
    return null;
  }

  @action.bound
  async fetchTemplates() {
    const res = await makeApi({ url: URLS.agentTemplates, method: "GET" });
    return this.ok(res) ? res.data : [];
  }

  // Show an agent the moment it is opened: the list (or the create call) already has it in
  // memory, so the page can render from that while getAgent refreshes. Runs are cleared so the
  // previous agent's runs never flash under this one.
  @action.bound
  seedAgent(id) {
    const same = a => a && String(a.id) === String(id);
    const hit = (same(this.currentAgent) && this.currentAgent) || (this.agents || []).find(same) || null;
    this.currentAgent = hit;
    this.runs = [];
    return hit;
  }

  // For the copilot drawer: read an agent / its runs without touching currentAgent / runs,
  // which the agent pages render (opening agent B in the drawer must not swap page A's data).
  async peekAgent(id) {
    const res = await makeApi({ url: this.fill(URLS.agentGet, { id }), method: "GET" });
    return this.ok(res) ? res.data : null;
  }

  async peekRuns(id, limit = 5) {
    const res = await makeApi({ url: `${this.fill(URLS.agentRuns, { id })}?limit=${limit}`, method: "GET" });
    return this.ok(res) && Array.isArray(res.data) ? res.data : [];
  }

  @action.bound
  async getAgent(id) {
    const res = await makeApi({ url: this.fill(URLS.agentGet, { id }), method: "GET" });
    // Clear on failure (missing, or not this user's) so a page never shows a previously
    // opened agent under a different id.
    this.currentAgent = this.ok(res) ? res.data : null;
    return this.currentAgent;
  }

  @action.bound
  async createDailyIcpProspector(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({
      url: URLS.agentCreateDailyIcpProspector,
      method: "POST",
      data: payload
    });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  @action.bound
  async fetchContactLists() {
    const res = await makeApi({ url: URLS.agentPickContactLists, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) {
      this.contactLists = res.data;
      return this.contactLists;
    }
    // The picker sits behind the agents gate (403 when agents are off), but Signals
    // uses these lists too: fall back to the plain contact-lists endpoint.
    const plain = await makeApi({ url: URLS.getContactList, method: "POST", data: { limit: 250, pageNo: 1 } });
    const rows = plain?.data?.listResponse?.list;
    // That endpoint leads with an "All" row (every contact), which is not a list anyone picked.
    if (Array.isArray(rows))
      this.contactLists = rows
        .filter(l => l.id && !/^all$/i.test(String(l.name || "").trim()))
        .map(l => ({ id: l.id, name: l.name, count: l.count || 0, createTs: l.createTs || 0 }));
    return this.contactLists;
  }

  // Creates a contact list (same endpoint as Contacts → New list) and adds it to the pickers.
  // Resolves to { list } or { error } with a message to show under the name field.
  @action.bound
  async createContactList(name) {
    const res = await makeApi({ url: URLS.createContactList, method: "POST", data: { listName: name } });
    const lr = res?.data?.listResponse;
    if (lr?.statusCode === 409) return { error: "A list with this name already exists." };
    const list = lr?.success ? lr.list?.[0] : null;
    if (!list?.id) return { error: "Couldn’t create the list. Please try again." };
    this.contactLists = [{ id: list.id, name: list.name, count: 0 }, ...this.contactLists];
    return { list };
  }

  @action.bound
  async fetchCrms() {
    const res = await makeApi({ url: URLS.agentPickCrms, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.crms = res.data;
    return this.crms;
  }

  // The events an agent can react to, with the filters each one offers (stage changed -> stages).
  @action.bound
  async fetchTriggerTypes() {
    if (this.triggerTypes.length) return this.triggerTypes;
    const res = await makeApi({ url: URLS.agentTriggerTypes, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.triggerTypes = res.data;
    return this.triggerTypes;
  }

  @action.bound
  async fetchContactStages() {
    const res = await makeApi({ url: URLS.agentPickContactStages, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.contactStages = res.data;
    return this.contactStages;
  }

  @action.bound
  async fetchContactFields() {
    const res = await makeApi({ url: URLS.agentPickContactFields, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.contactFields = res.data;
    return this.contactFields;
  }

  // The team's Slack channels. An empty list means Slack isn't connected, which is why the
  // "loaded" flag is separate — the UI has to tell "no Slack" from "not asked yet".
  @action.bound
  async fetchSlackChannels() {
    const res = await makeApi({ url: URLS.agentPickSlackChannels, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.slackChannels = res.data;
    this.slackChannelsLoaded = true;
    return this.slackChannels;
  }

  // Sequences as {value, label}, for the "only in these sequences" trigger filter.
  @action.bound
  async fetchSequencesLite() {
    const res = await makeApi({ url: URLS.sequencesLite, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) {
      this.sequencesLite = res.data.map(s => ({ value: s.id, label: unescapeName(s.name) }));
    }
    return this.sequencesLite;
  }

  @action.bound
  async fetchSavedSearches() {
    const res = await makeApi({ url: URLS.agentPickSavedSearches, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.savedSearches = res.data;
    return this.savedSearches;
  }

  @action.bound
  async fetchTeamMembers() {
    const res = await makeApi({ url: URLS.agentPickTeamMembers, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.teamMembers = res.data;
    return this.teamMembers;
  }

  @action.bound
  async fetchTeamGroups() {
    const res = await makeApi({ url: URLS.agentPickTeamGroups, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.teamGroups = res.data;
    return this.teamGroups;
  }

  @action.bound
  async fetchAccountLists() {
    const res = await makeApi({ url: URLS.agentPickAccountLists, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.accountLists = res.data;
    return this.accountLists;
  }

  async createAccountProspector(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateAccountProspector, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  @action.bound
  async fetchConditionFields() {
    const res = await makeApi({ url: URLS.agentConditionFields, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.conditionFields = res.data;
    return this.conditionFields;
  }

  // The step registry is static per deploy: fetch it once, then every page names steps instantly.
  stepTypesCache = null;

  @action.bound
  async fetchStepTypes() {
    if (this.stepTypesCache) return this.stepTypesCache;
    const res = await makeApi({ url: URLS.agentStepTypes, method: "GET" });
    const list = this.ok(res) && Array.isArray(res.data) ? res.data : [];
    if (list.length) this.stepTypesCache = list;
    return list;
  }

  @action.bound
  async createAgent(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreate, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  // Apply a plain-English change to a builder draft. Returns { agent, changes } or { error }.
  // Not saved: the builder shows the result and the user saves as usual.
  async refineAgent(draft, instruction) {
    const res = await makeApi({ url: URLS.agentRefine, method: "POST", data: { draft, instruction } });
    if (this.ok(res) && res.data && res.data.agent) {
      return { agent: res.data.agent, changes: res.data.changes || [] };
    }
    const body = (res && res.data) || (res && res.response && res.response.data) || {};
    return { error: body.error || "Couldn't make that change right now. Please try again." };
  }

  @action.bound
  async generateAgent(prompt) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentGenerate, method: "POST", data: { prompt } });
    this.busy = false;
    // Success: a 200 carrying the draft agent (which never has an `error` field).
    if (this.ok(res) && res.data && !res.data.error) {
      this.generatedDraft = res.data;
      this.generatedPrompt = prompt || "";
      return res.data;
    }
    // Failure: surface the server's friendly reason. makeApi returns the axios error on
    // non-2xx, so the body lives under res.response.data for 4xx/5xx.
    const serverMsg =
      (res && res.data && res.data.error) ||
      (res && res.response && res.response.data && res.response.data.error);
    this.error = serverMsg || "Couldn't generate that agent. Try rephrasing your description.";
    return null;
  }

  @action.bound
  // Take the staged draft only if it's for the given template (so another screen's draft is left alone).
  consumeTemplateDraft(templateKey) {
    if (!this.generatedDraft || this.generatedDraft.templateKey !== templateKey) return null;
    return this.consumeGeneratedDraft();
  }

  consumeGeneratedDraft() {
    const d = this.generatedDraft;
    this.generatedDraft = null;
    // Keep the prompt: the review page renders after this runs, and it's cleared on leaving.
    return d;
  }

  // The review page is done with the typed prompt — drop it so it can't leak into the next draft.
  @action.bound
  clearGeneratedPrompt() {
    this.generatedPrompt = "";
  }

  // Stage a composed template as the builder draft (for "Customize steps" → canvas).
  @action.bound
  stageDraft(draft) {
    this.generatedDraft = draft;
  }

  // Compose (but don't create) a template's flow from the given inputs, for pre-create editing.
  @action.bound
  async composeTemplate(key, payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: this.fill(URLS.agentComposeTemplate, { key }), method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res) && res.data) return res.data;
    this.error = "Could not open this template for editing.";
    return null;
  }

  @action.bound
  async createEventAgent(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({
      url: URLS.agentCreateEventAgent,
      method: "POST",
      data: payload
    });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  // The templates offered for a signal type: [{key, name, description, steps, needsSequence}].
  @action.bound
  // `savedSignalId` lets the server leave out templates for signals agents can't run on (competitor signals).
  async fetchSignalTemplates(signalType, savedSignalId = null) {
    const params = savedSignalId ? { signalType, savedSignalId } : { signalType };
    const res = await makeApi({ url: URLS.agentSignalTemplates, method: "GET", params });
    return this.ok(res) && Array.isArray(res.data) ? res.data : [];
  }

  // An agent that starts when one of the member's saved signals fires.
  @action.bound
  async createSignalAgent(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateSignalAgent, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  async createListProspector(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateListProspector, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  async createCompanyProspector(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateCompanyProspector, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  async createLookalikeProspector(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateLookalikeProspector, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  async createLinkedinImport(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateLinkedinImport, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  async createActivityReport(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateActivityReport, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  async createReplyRouter(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateReplyRouter, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  @action.bound
  async previewIcp(id) {
    // Background fill-in for the ICP panel: must not flip `busy`, or it locks Test run / Run now while it loads.
    const res = await makeApi({ url: this.fill(URLS.agentIcpPreview, { id }), method: "GET" });
    if (this.ok(res)) {
      this.icpPreview = res.data;
      return res.data;
    }
    return null;
  }

  // Describe + preview List Builder filter state for a drafted search step (after refining it in List Builder).
  async previewListBuilderSearch(state, companies) {
    const res = await makeApi({ url: URLS.agentSearchPreview, method: "POST", data: { state, companies: !!companies } });
    return this.ok(res) && res.data && res.data.state ? res.data : null;
  }

  // Use a List Builder saved search as the agent's ICP (this approves the ICP).
  @action.bound
  async setIcpSavedSearch(id, savedSearchId) {
    this.busy = true;
    const res = await makeApi({
      url: this.fill(URLS.agentIcpSavedSearch, { id }),
      method: "POST",
      data: { savedSearchId }
    });
    this.busy = false;
    if (this.ok(res) && res.data && res.data.id) {
      if (this.currentAgent && this.currentAgent.id === id) this.currentAgent = res.data;
      return res.data;
    }
    return null;
  }

  @action.bound
  async setStatus(id, status) {
    const res = await makeApi({
      url: this.fill(URLS.agentSetStatus, { id, status }),
      method: "POST"
    });
    if (this.ok(res)) {
      if (this.currentAgent && this.currentAgent.id === id) this.currentAgent = res.data;
      this.agents = this.agents.map(a => (a.id === id ? res.data : a));
      return res.data;
    }
    return null;
  }

  @action.bound
  async deleteAgent(id) {
    this.busy = true;
    const res = await makeApi({ url: this.fill(URLS.agentDelete, { id }), method: "DELETE" });
    this.busy = false;
    if (this.ok(res)) {
      this.agents = this.agents.filter(a => a.id !== id);
      if (this.currentAgent && this.currentAgent.id === id) this.currentAgent = null;
      // The list renders from the overview and the "N of M" line from usage: update both now, don't wait on a refetch.
      if (this.overview && Array.isArray(this.overview.agents)) {
        this.overview = { ...this.overview, agents: this.overview.agents.filter(c => !c.agent || c.agent.id !== id) };
      }
      if (this.usage && this.usage.used > 0) this.usage = { ...this.usage, used: this.usage.used - 1 };
      this.fetchUsage();
      return true;
    }
    toasterState.setToastMsg("Could not delete the agent. Try again.", "failure");
    return false;
  }

  @action.bound
  async updateAgent(id, changes) {
    this.busy = true;
    const res = await makeApi({ url: this.fill(URLS.agentUpdate, { id }), method: "PUT", data: changes });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      this.agents = this.agents.map(a => (a.id === id ? res.data : a));
      return res.data;
    }
    return null;
  }

  @action.bound
  async fetchFolders() {
    const res = await makeApi({ url: URLS.agentFolders, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.folders = res.data;
    return this.folders;
  }

  @action.bound
  async createFolder(name) {
    const res = await makeApi({ url: URLS.agentFolders, method: "POST", data: { name } });
    if (this.ok(res) && Array.isArray(res.data)) this.folders = res.data;
    return this.folders;
  }

  @action.bound
  async renameFolder(oldName, newName) {
    const res = await makeApi({ url: URLS.agentFolders, method: "PUT", data: { oldName, newName } });
    if (this.ok(res) && Array.isArray(res.data)) { this.folders = res.data; await this.fetchAgents(); }
    return this.folders;
  }

  @action.bound
  async deleteFolder(name) {
    const res = await makeApi({ url: URLS.agentFolderDelete, method: "POST", data: { name } });
    if (this.ok(res) && Array.isArray(res.data)) { this.folders = res.data; await this.fetchAgents(); }
    return this.folders;
  }

  @action.bound
  async setFolder(id, folder) {
    const res = await makeApi({
      url: this.fill(URLS.agentSetFolder, { id, folder: folder || "" }),
      method: "POST"
    });
    if (this.ok(res)) {
      this.currentAgent = res.data;
      this.agents = this.agents.map(a => (a.id === id ? res.data : a));
      return res.data;
    }
    return null;
  }

  @action.bound
  async testRun(id) {
    this.testing = true;
    const res = await makeApi({ url: this.fill(URLS.agentTest, { id }), method: "POST" });
    this.testing = false;
    if (this.ok(res)) {
      this.lastRun = res.data;
      return res.data;
    }
    return null;
  }

  isTerminalRun(status) {
    // SLEEPING = parked on a wait step or a background job — settled until the wait scanner resumes it.
    return ["SUCCEEDED", "FAILED", "PARTIAL", "CANCELLED", "WAITING_APPROVAL", "SLEEPING"].includes(status);
  }

  @action.bound
  async runNow(id, inputs) {
    this.busy = true;
    const res = await makeApi({ url: this.fill(URLS.agentRun, { id }), method: "POST", data: inputs || {} });
    if (!this.ok(res) || !res.data) {
      this.busy = false;
      return null;
    }
    // Runs are async now — the POST returns a QUEUED run. Poll until it's terminal,
    // refreshing the runs list each cycle so the UI shows live progress.
    const runId = res.data.id;
    this.lastRun = res.data;
    const deadline = Date.now() + 5 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 3000));
      const runs = await this.fetchRuns(id);
      const current = (runs || []).find(x => x.id === runId);
      if (current) {
        this.lastRun = current;
        if (this.isTerminalRun(current.status)) break;
      }
    }
    this.busy = false;
    return this.lastRun;
  }

  @action.bound
  async fetchApprovals() {
    this.approvalsLoading = true;
    const res = await makeApi({ url: URLS.agentApprovals, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) {
      this.approvals = res.data;
      this.pendingApprovalCount = res.data.length;
    }
    this.approvalsLoading = false;
    return this.approvals;
  }

  @action.bound
  async fetchApprovalCount() {
    const res = await makeApi({ url: URLS.agentApprovalsCount, method: "GET" });
    if (this.ok(res) && res.data) {
      this.pendingApprovalCount = res.data.count || 0;
    }
    return this.pendingApprovalCount;
  }

  @action.bound
  async approveBatch(id, contactIds) {
    this.busy = true;
    const res = await makeApi({
      url: this.fill(URLS.agentApprovalApprove, { id }),
      method: "POST",
      data: contactIds ? { contactIds } : {}
    });
    this.busy = false;
    if (this.ok(res)) {
      this.approvals = this.approvals.filter(a => a.id !== id);
      this.pendingApprovalCount = this.approvals.length;
      return res.data || true;
    }
    return null;
  }

  @action.bound
  async rejectBatch(id) {
    this.busy = true;
    const res = await makeApi({
      url: this.fill(URLS.agentApprovalReject, { id }),
      method: "POST"
    });
    this.busy = false;
    if (this.ok(res)) {
      this.approvals = this.approvals.filter(a => a.id !== id);
      this.pendingApprovalCount = this.approvals.length;
      return res.data || true;
    }
    return null;
  }

  @action.bound
  async fetchRuns(id, limit = 20) {
    const res = await makeApi({
      url: `${this.fill(URLS.agentRuns, { id })}?limit=${limit}`,
      method: "GET"
    });
    if (this.ok(res) && Array.isArray(res.data)) {
      this.runs = res.data;
    }
    return this.runs;
  }

  @action.bound
  async fetchRunRecords(id, runId) {
    this.runRecordsLoading = { ...this.runRecordsLoading, [runId]: true };
    const res = await makeApi({
      url: this.fill(URLS.agentRunRecords, { id, runId }),
      method: "GET"
    });
    if (this.ok(res) && Array.isArray(res.data)) {
      this.runRecords = { ...this.runRecords, [runId]: res.data };
    }
    this.runRecordsLoading = { ...this.runRecordsLoading, [runId]: false };
    return this.runRecords[runId] || [];
  }
}

export const agentsData = new AgentsData();
export default agentsData;
